import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import Toastify from 'toastify-js';
import 'toastify-js/src/toastify.css';
import './RegisterPage.css';
import UserAuthContext from '../../context/UserAuthContext';
import ENDPOINTS from '../../endpoints';
import { BeatLoader } from 'react-spinners';

const RegisterPage: React.FC = () => {
  const { login } = useContext(UserAuthContext);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sponsorName, setSponsorName] = useState('');
  const [termsAgreedUS, setTermsAgreedUS] = useState(false);
  const [termsAgreedUAE, setTermsAgreedUAE] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [sponsorNameError, setSponsorNameError] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  
  const getQueryParams = (param: string) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  };

  useEffect(() => {
    const sponsorFromURL = getQueryParams('sponsor');
    if (sponsorFromURL) {
      setSponsorName(sponsorFromURL);
    }
  }, []);

  const handleRegister = async (e: React.FormEvent) => {
    setLoading(true);
    e.preventDefault();
    setUsernameError('');
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');
    setSponsorNameError('');
// Add this regex check right after basic empty validations
    const usernameRegex = /^[a-zA-Z0-9]+$/;

    if (!usernameRegex.test(username)) {
      setUsernameError('Username must be alphanumeric only (no spaces or symbols)');
      setLoading(false);
      return;
    }

    if (!username || !email || !password || password !== confirmPassword || !sponsorName) {
      if (!username) setUsernameError('Username is required');
      if (!email) setEmailError('Email is required');
      if (!password) setPasswordError('Password is required');
      if (password !== confirmPassword) setConfirmPasswordError('Passwords do not match');
      if (!sponsorName) setSponsorNameError('Sponsor name is required');
      setLoading(false);
      return;
    }

    if (!termsAgreedUS && !termsAgreedUAE) {
      Toastify({
        text: "Please agree to the terms and conditions",
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: "top",
        position: "center",
        stopOnFocus: true,
        style: {
          background: "linear-gradient(to right, #FF0000, #CB4335)",
        },
      }).showToast();
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(ENDPOINTS.register, {
        user_name: username,
        email,
        password,
        sponsor_name: sponsorName,
      });
      console.log(response.data.success);
      console.log('Testing');

      if (response.status ===  200) {
        Toastify({
          text: response.data.message || "User registered successfully!",
          duration: 3000,
          newWindow: true,
          close: true,
          gravity: "top",
          position: "center",
          stopOnFocus: true,
          style: {
            background: "linear-gradient(to right, #00b09b, #96c93d)",
          },
        }).showToast();

        // Trigger OTP modal
        setShowOtpModal(true);

        // const { data } = response.data; // Extract data
        // login(data);

        // // After successful registration, navigate to the dashboard
        // setTimeout(() => {
        //   window.location.href = '/dashboard';
        // }, 2000);
      } else {
        Toastify({
          text: response.data.error || "Registration failed",
          duration: 3000,
          newWindow: true,
          close: true,
          gravity: "top",
          position: "center",
          stopOnFocus: true,
          style: {
            background: "linear-gradient(to right, #FF0000, #CB4335)",
          },
        }).showToast();
      }
    } catch (error: any) {
      let errorMessage = 'Registration failed. Please try again.';
      if (axios.isAxiosError(error) && error.response) {
        errorMessage = error.response.data.error || errorMessage;
      }
      Toastify({
        text: errorMessage,
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: "top",
        position: "center",
        stopOnFocus: true,
        style: {
          background: "linear-gradient(to right, #FF0000, #CB4335)",
        },
      }).showToast();
    }
  };

  const handleOtpVerification = async () => {
    setOtpError('');
    if (!otp) {
      setOtpError('OTP is required');
      return;
    }
    setVerifyLoading(true);
    try {
      const response = await axios.post(ENDPOINTS.verifyOTP, { email, otpCode: otp });
      if (response.status === 200) {
        Toastify({
          text: "OTP verified successfully!",
          duration: 3000,
          newWindow: true,
          close: true,
          gravity: "top",
          position: "center",
          stopOnFocus: true,
          style: {
            background: "linear-gradient(to right, #00b09b, #96c93d)",
          },
        }).showToast();
        login(response.data);
        window.location.href = '/dashboard';
      } else {
        throw new Error(response.data.error || 'OTP verification failed');
      }
    } catch (error: any) {
      Toastify({
        text: error.response?.data?.error || "OTP verification failed",
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: "top",
        position: "center",
        stopOnFocus: true,
        style: {
          background: "linear-gradient(to right, #FF0000, #CB4335)",
        },
      }).showToast();
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const response = await axios.post(ENDPOINTS.generateOTP, { 
        email,
        type: 'register',
        resend: true,
      });
      setResendLoading(true);
      if (response.status === 200) {
        Toastify({
          text: "OTP resent successfully!",
          duration: 3000,
          newWindow: true,
          close: true,
          gravity: "top",
          position: "center",
          stopOnFocus: true,
          style: {
            background: "linear-gradient(to right, #00b09b, #96c93d)",
          },
        }).showToast();
      }
    } catch (error: any) {
      Toastify({
        text: error.response?.data?.error || "Failed to resend OTP",
        duration: 3000,
        newWindow: true,
        close: true,
        gravity: "top",
        position: "center",
        stopOnFocus: true,
        style: {
          background: "linear-gradient(to right, #FF0000, #CB4335)",
        },
      }).showToast();
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-image-section">
        <h1>Smart CrowdFunding Platform!</h1>
        <img src={require('../../assets/images/register.png')} alt="Trading Illustration" className="register-image" />
        <p>Join The Fastest Growing Crowdfunding Community Today</p>
      </div>
      <div className="register-form-section">
        <h2>Create Your Account</h2>
        <form onSubmit={handleRegister}>
          <div className="input-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              placeholder="Please enter your Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
            {usernameError && <span className="error-message">{usernameError}</span>}
          </div>
          <div className="input-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              placeholder="Please enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {emailError && <span className="error-message">{emailError}</span>}
          </div>
          <div className="input-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              placeholder="Set password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {passwordError && <span className="error-message">{passwordError}</span>}
          </div>
          <div className="input-group">
            <label htmlFor="confirm-password">Confirm Password</label>
            <input
              type="password"
              id="confirm-password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            {confirmPasswordError && <span className="error-message">{confirmPasswordError}</span>}
          </div>
          <div className="input-group">
            <label htmlFor="sponsor">Sponsor Name</label>
            <input
              type="text"
              id="sponsor"
              placeholder="Enter sponsor name"
              value={sponsorName}
              onChange={(e) => setSponsorName(e.target.value)}
              required
            />
            {sponsorNameError && <span className="error-message">{sponsorNameError}</span>}
          </div>
          <div className="terms">
          <input
            type="checkbox"
            id="termsUS"
            checked={termsAgreedUS}
            onChange={(e) => setTermsAgreedUS(e.target.checked)}
            required
          />
          <label htmlFor="termsUS">
            I hereby confirm that I am not a US citizen
          </label>
        </div>
        <div className="terms">
          <input
            type="checkbox"
            id="termsUAE"
            checked={termsAgreedUAE}
            onChange={(e) => setTermsAgreedUAE(e.target.checked)}
            required
          />
          <label htmlFor="termsUAE">
            I hereby confirm that I am not a UAE citizen
          </label>
        </div>
          <button type="submit" className="register-button">Register</button>
        </form>
        <p className="login-link">
          Already registered? <a href="/login">Log In</a>
        </p>
      </div>
      {showOtpModal && (
        <div className="otp-modal">
          <div className="otp-modal-content">
            <h3>Verify OTP</h3>
            <input
              type="text"
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
            {otpError && <span className="error-message">{otpError}</span>}
            <button
              className="verify-button"
              onClick={handleOtpVerification}
              disabled={verifyLoading}
            >
            {verifyLoading ? <BeatLoader size={8} color={"#ffffff"} /> : 'Verify OTP'}
            </button>
            <button
              className="resend-button"
              onClick={handleResendOtp}
              disabled={resendLoading}
            >
              {resendLoading ? <BeatLoader size={8} color={"#ffffff"} /> : 'Resend OTP'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;